/**
 * Platform-managed storage settings.
 *
 * The platform's own R2 credentials come from the environment, not from the
 * per-merchant credential table: they are one set of platform infrastructure
 * keys, not merchant data, and storing a copy encrypted against every
 * organization would multiply the blast radius of a single key by the number
 * of merchants for no benefit.
 */
export interface PlatformStorageEnv {
  bucket: string;
  region: string;
  endpoint: string;
  accessKeyId: string;
  secretAccessKey: string;
  publicBaseUrl: string;
}

export function readPlatformStorageEnv(): PlatformStorageEnv | null {
  const bucket = process.env.PLATFORM_R2_BUCKET;
  const accessKeyId = process.env.PLATFORM_R2_ACCESS_KEY_ID;
  const secretAccessKey = process.env.PLATFORM_R2_SECRET_ACCESS_KEY;
  const endpoint = process.env.PLATFORM_R2_ENDPOINT;
  const publicBaseUrl = process.env.PLATFORM_R2_PUBLIC_BASE_URL;

  if (!bucket || !accessKeyId || !secretAccessKey || !endpoint || !publicBaseUrl) return null;

  return {
    bucket,
    region: process.env.PLATFORM_R2_REGION ?? 'auto',
    endpoint,
    accessKeyId,
    secretAccessKey,
    publicBaseUrl: publicBaseUrl.replace(/\/+$/, ''),
  };
}

/**
 * Bucket names that name an environment rather than a project.
 *
 * A deployment using one bucket per environment calls them this; a deployment
 * using a single shared bucket calls it something else entirely
 * (`forgestack-media`), and the check below then has nothing to say.
 */
const ENVIRONMENT_BUCKETS = ['test', 'production'];

/**
 * Refuses to start when the bucket and the key prefix disagree about which
 * environment this is.
 *
 * The two are meant to be redundant: `production` objects live in the
 * `production` bucket under a `production/` prefix. Redundancy is only worth
 * having if a disagreement is caught, and this is the disagreement that
 * matters — an R2 token with account-wide object access reaches every bucket,
 * so `PLATFORM_R2_BUCKET=production` on a developer's machine is enough to
 * write into live merchant media. Nothing about that fails on its own: the
 * upload succeeds, the URL resolves, and the object simply should not be there.
 *
 * Failing at boot rather than at upload time is deliberate. A misconfigured
 * server that refuses to start gets noticed in seconds; one that starts and
 * writes to the wrong bucket gets noticed when someone asks why test images are
 * on the live site.
 *
 * Silent when the bucket is not named after an environment — a single-bucket
 * deployment separated only by prefix is a legitimate setup, and this check has
 * no opinion on it.
 */
export function assertStorageEnvironmentConsistent(): void {
  const bucket = process.env.PLATFORM_R2_BUCKET?.trim().toLowerCase();
  const prefix = (process.env.MEDIA_ENV_PREFIX ?? 'test').trim().toLowerCase();

  if (!bucket || !ENVIRONMENT_BUCKETS.includes(bucket)) return;
  if (bucket === prefix) return;

  throw new Error(
    `Storage environment mismatch: PLATFORM_R2_BUCKET="${bucket}" but ` +
      `MEDIA_ENV_PREFIX="${prefix}". These must agree — a bucket named after an ` +
      `environment is that environment. Writing "${prefix}" data into the ` +
      `"${bucket}" bucket is almost certainly a mistake, and an R2 token with ` +
      `account-wide object access would let it succeed silently. ` +
      `Set both to "${bucket}", or both to "${prefix}".`
  );
}

export function isPlatformStorageConfigured(): boolean {
  return readPlatformStorageEnv() !== null;
}

/**
 * A separate key pair used when presigning against the platform bucket.
 *
 * A presigned URL carries its access key ID in the query string, so every
 * merchant browser sees whichever key signed it. Keeping that separate from the
 * key the server uses for backups and migrations means the exposed one can be
 * revoked on its own — R2 offers no write-only token type, so the isolation is
 * the point rather than a narrower permission.
 *
 * Falls back to the main key when unset so a partially-configured environment
 * still works, degrading disclosure rather than availability.
 */
export function readPresignCredentials(env: PlatformStorageEnv): { accessKeyId: string; secretAccessKey: string } {
  const accessKeyId = process.env.PLATFORM_R2_UPLOAD_ACCESS_KEY_ID;
  const secretAccessKey = process.env.PLATFORM_R2_UPLOAD_SECRET_ACCESS_KEY;
  if (accessKeyId && secretAccessKey) return { accessKeyId, secretAccessKey };
  return { accessKeyId: env.accessKeyId, secretAccessKey: env.secretAccessKey };
}

export const STORAGE_MODES = ['platform_r2', 'byos_r2', 'byos_s3', 'local_disk'] as const;
export type StorageMode = (typeof STORAGE_MODES)[number];

export const CONFIG_STATUSES = ['active', 'retained', 'archived'] as const;
export type ConfigStatus = (typeof CONFIG_STATUSES)[number];

export function isByos(mode: string): boolean {
  return mode === 'byos_r2' || mode === 'byos_s3';
}

/** Quota is charged for platform-managed bytes only (plan §17). */
export function countsAgainstQuota(mode: string): boolean {
  return mode === 'platform_r2' || mode === 'local_disk';
}
